/**
 * Filtering for the request/response payloads PostHog session replay captures.
 *
 * Checkout is a payment page, so this fails closed: a request only carries a
 * payload into the recording if its endpoint is named in `REDACTED_ENDPOINTS`.
 * Anything unrecognised - including a route the backend adds tomorrow - keeps
 * its URL, status and timing but loses headers and both bodies.
 *
 * Four tiers, in the order they are applied:
 *
 *  1. `DENIED_HOST_SUFFIXES` / `DENIED_PATH_PATTERNS` - dropped entirely.
 *     Third parties are matched by host rather than by an explicit URL list,
 *     because their URLs are versioned, sharded and impossible to enumerate.
 *  2. `NEVER_PAYLOAD_ENDPOINTS` - checked *before* the allowlist, so a payment
 *     route cannot be opened up by carelessly adding it to `REDACTED_ENDPOINTS`.
 *  3. `REDACTED_ENDPOINTS` - our own routes that are worth reading: query string
 *     stripped, sensitive headers masked, PII fields in JSON bodies replaced.
 *  4. Everything else - URL, status and timing only.
 *
 * Endpoints are written as the same path templates the generated services use
 * (`@libs/client/order/services/cmsOrder.ts` and friends), so the tables can be
 * diffed against the source of truth by eye.
 *
 * `CapturedNetworkRequest` is not re-exported by posthog-js, so this works
 * against a minimal structural type and stays generic - the real type from
 * posthog-js satisfies it and flows straight through.
 */

export interface NetworkRequestLike {
  name: string
  requestHeaders?: Record<string, string>
  responseHeaders?: Record<string, string>
  requestBody?: string | null
  responseBody?: string | null
  isInitial?: boolean
}

export const REDACTED = '[redacted]'
const REDACTED_BODY = '[redacted body]'
const REDACTED_OPAQUE_BODY = '[redacted non-json body]'

/**
 * Third parties dropped outright. Matched as a hostname suffix, not as a
 * substring of the URL: `m.stripe.network` and hyphenated hosts are exactly what
 * a loose pattern misses, and a suffix match cannot be fooled by a path that
 * happens to contain `stripe.com`.
 *
 * Payment methods in use are Stripe, Apple Pay and PayPal. Apple Pay runs
 * through the Stripe SDK (`PayCreditcardJsService.cj`), so `stripe.com` covers
 * it - there is no separate Apple host to list.
 */
const DENIED_HOST_SUFFIXES = [
  // Card numbers, tokens, client secrets. Covers js. / api. / m. / r. /
  // merchant-ui-api. subdomains.
  'stripe.com',
  'stripe.network',
  // Covers www. / c. / t. / www.sandbox. subdomains.
  'paypal.com',
  'paypalobjects.com',
  // Address autocomplete - the query string is literally what the customer types.
  'maps.googleapis.com',
  // PostHog's own ingestion and asset hosts - recording these is recursive noise.
  'posthog.com',
  // Sentry envelopes carry breadcrumbs, which can pick up PII.
  'sentry.selless.dev'
]

/**
 * Our own Kong beacon: already in PostHog as events, and its query string
 * carries the flattened shipping address.
 */
const DENIED_PATH_PATTERNS: RegExp[] = [/\/v1\/debug\//i]

/**
 * Payment, credential and address routes. These never carry a payload into a
 * recording, whatever else the tables say.
 */
const NEVER_PAYLOAD_ENDPOINTS = [
  // Stripe card charge, and Apple Pay via the same endpoint.
  '/cart/v1/pay/cj/{id}',
  // Returns a Stripe client_secret.
  '/cart/v1/pay/cj/{id}/setup-intent',
  // Saved cards: brand and last4.
  '/cart/v1/payment/payment-methods',
  // Device push token.
  '/notification/v1/token',
  '/notification/v1/token/deactivate'
]

/**
 * Routes whose payload is worth reading in a replay. `PII_BODY_KEYS` covers every
 * PII field on `CustomerAddress`, which leaves the amounts, ids and error codes
 * that make a failed checkout diagnosable.
 *
 * Only add a route here after checking its body type: key-name redaction is blind
 * to PII carried under a generic key name.
 */
const REDACTED_ENDPOINTS = [
  // Cart / order.
  '/cart/v1',
  '/cart/v1/{id}',
  '/cart/v1/{id}/discount',
  '/cart/v1/{id}/insurance',
  '/cart/v1/{id}/opt-out',
  '/cart/v1/abandoned',
  // Address routes. Their `{ field, value }` shape needs the sibling rule in
  // `redactValue` - key-name redaction alone is blind to it. Full-fidelity
  // address capture is the backend's job, via its own request logs.
  '/cart/v1/{id}/shipping',
  '/cart/v1/{id}/billing',
  '/cart/v1/{id}/collect-tax',
  '/cart/v1/estimate-tax',
  // PayPal. Card data never touches these - PayPal handles it on their side.
  // Requests are trivial (`{ return_url }`, `{ order_id }`); the responses are
  // where the value is: order code, order id, and the capture error status that
  // tells you why a confirmed PayPal payment still failed. `approve` does return
  // a `shipping` address, but as a properly-named `CustomerAddress`, which
  // `PII_BODY_KEYS` covers field for field.
  '/cart/v1/pay/px',
  '/cart/v1/pay/px/{id}/create',
  '/cart/v1/pay/px/{id}/approve',
  '/cart/v1/pay/px/{id}/cancel',
  // Gateway: product and configuration data, no customer PII.
  '/gw/v1/configuration',
  '/gw/v1/configuration/feature',
  '/gw/v1/configuration/paygates',
  '/gw/v1/products',
  '/gw/v1/products/{id}',
  '/gw/v1/products/{id}/metadata',
  '/gw/v1/products/{id}/reviews',
  '/gw/v1/products/{id}/checkout-page-reviews',
  '/gw/v1/products/variants/lookup',
  // Notification: ids only.
  '/notification/v1/delivered',
  '/notification/v1/saved-amount-calculated'
]

/**
 * Header values replaced with `[redacted]`. The header name stays visible, so you
 * can still see it was present.
 *
 * The `x-*` group is the encrypted-payload envelope from `Packet`
 * (@libs/common/models/types/cryptography.d.ts), and it is more dangerous than a
 * normal secret header: `deriveAesKey` builds the AES-GCM key out of nothing but
 *
 *     SHA-256(x-rid + x-nonce + x-timestamp)
 *
 * with no server-side secret involved. Anyone holding the full set can decrypt
 * the payment payload offline, so capturing these into a replay is equivalent to
 * capturing the plaintext.
 *
 * `x-nonce` is the high-entropy input, so masking it alone already makes the key
 * unobtainable; `x-timestamp` goes too because a timestamp is guessable within a
 * window, and the ciphertext trio goes because it is the payload itself.
 * `x-rid` and `x-version` are kept - useless as key material once the nonce is
 * gone, and `x-rid` is the one field worth reading to correlate with backend logs.
 *
 * If `deriveAesKey` ever changes which headers it consumes, revisit this list.
 */
const SENSITIVE_HEADERS = new Set([
  'authorization',
  'proxy-authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-auth-token',
  'x-csrf-token',
  // Encrypted payload envelope - see the note above.
  'x-ctext',
  'x-iv',
  'x-tag',
  'x-nonce',
  'x-timestamp'
])

/**
 * Body fields replaced wherever they appear, at any depth. Mirrors every PII
 * field on `CustomerAddress` (@libs/client/order/services/cmsOrder.ts) plus the
 * card and secret fields.
 *
 * Deliberately does NOT include bare `name` or `number`: those are product names
 * and order numbers here, and nuking them would gut the value of capturing at
 * all. `country` / `country_code` are also kept - coarse geo, and needed to
 * debug tax.
 *
 * The PayPal-native names are defensive. `/cart/v1/pay/px/{id}/approve` returns
 * `any`, and nothing in this repo maps `email_address` / `given_name` / friends -
 * so it cannot be ruled out that the backend proxies PayPal's raw payer object
 * through. Listing a key that never arrives costs nothing.
 */
const PII_BODY_KEYS = new Set([
  'email',
  'phone',
  'phone_number',
  // PayPal / PSP naming variants.
  'email_address',
  'given_name',
  'surname',
  'recipient_name',
  'address_line_1',
  'address_line_2',
  'admin_area_1',
  'admin_area_2',
  'national_number',
  'client_ip',
  'ip',
  'ip_address',
  'first_name',
  'last_name',
  'full_name',
  'address',
  'address1',
  'address2',
  'street',
  'city',
  'state',
  'province',
  'zip',
  'zipcode',
  'postal_code',
  'credit_card_info',
  'card',
  'card_number',
  // Stripe PaymentMethod ids. Not cardholder data - a `pm_` token carries no PAN
  // or CVC, which is the whole point of tokenisation - and not a credential,
  // since charging one needs a secret key. But it is a stable identifier tied to
  // one person and one saved card, and `saved_payment_method_id` is accepted in
  // the /pay/cj request body, so it is only as harmless as the backend's check
  // that the id belongs to the authenticated customer.
  //
  // Nothing is lost by masking: `useEventTracking` already reports only the last
  // 6 characters (`pmLast6` -> `payment_method_last6`), which is what correlation
  // actually needs. `payment_intent_id` is deliberately NOT here - a `pi_` is not
  // chargeable without its client_secret, and it is the field you look up in the
  // Stripe dashboard when a payment fails.
  'payment_method_id',
  'saved_payment_method_id',
  'stripe_payment_method_id',
  'cvv',
  'cvc',
  'exp_month',
  'exp_year',
  'client_secret',
  'password'
])

/**
 * Cart and order ids here are cuid-style: 25 lowercase alphanumerics, never
 * hyphenated (see the fixtures in `@libs/client/mocks/mockData.ts`).
 *
 * Requiring that shape matters. A permissive `[^/]+` would let `/cart/v1/{id}`
 * also match a route name - `/cart/v1/charge`, `/cart/v1/loyalty-points` - and
 * quietly re-open the allowlist to endpoints nobody has reviewed. An id that
 * does not fit falls through to the URL-only default, which is the safe way to
 * be wrong.
 */
const ID_SEGMENT = '[A-Za-z0-9]{16,}'

/**
 * Turns a service path template into an anchored matcher: `{id}` becomes an
 * id-shaped segment, every literal segment is escaped. Anchoring at both ends
 * means a near-miss degrades to the URL-only default rather than matching
 * something it shouldn't.
 */
function compilePathTemplate(template: string): RegExp {
  const pattern = template
    .split('/')
    .map((segment) =>
      /^\{.+\}$/.test(segment) ? ID_SEGMENT : segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    )
    .join('/')
  return new RegExp(`^${pattern}$`)
}

const NEVER_PAYLOAD_MATCHERS = NEVER_PAYLOAD_ENDPOINTS.map(compilePathTemplate)
const REDACTED_MATCHERS = REDACTED_ENDPOINTS.map(compilePathTemplate)

function stripQueryString(url: string) {
  const queryStart = url.indexOf('?')
  return queryStart === -1 ? url : url.slice(0, queryStart)
}

/**
 * Splits the URL into the two parts the rules match on. Path rules must run
 * against the pathname rather than the whole URL, or a trailing `?order=...`
 * sits between the path and the `$` anchor and silently defeats the match.
 *
 * An unparseable URL yields an empty hostname, so it is never dropped as a
 * third party - it falls through to the URL-only default instead.
 */
function parseUrl(url: string) {
  try {
    const parsed = new URL(url)
    return { hostname: parsed.hostname.toLowerCase(), pathname: parsed.pathname }
  } catch {
    return { hostname: '', pathname: stripQueryString(url) }
  }
}

const isDeniedHost = (hostname: string) =>
  DENIED_HOST_SUFFIXES.some((suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`))

function redactHeaders(headers?: Record<string, string>) {
  if (!headers) return headers
  return Object.entries(headers).reduce((result, [key, value]) => {
    result[key] = SENSITIVE_HEADERS.has(key.toLowerCase()) ? REDACTED : value
    return result
  }, {} as Record<string, string>)
}

function redactValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redactValue)
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)

    /**
     * `UpdatingCartShippingField` is `{ field, value }`, so the street, email or
     * phone arrives under a key literally called `value` - invisible to a PII key
     * list, and invisible to value-shape scanning too ("221B Baker St" matches no
     * email or phone pattern).
     *
     * `field` is typed `CustomerAddressField`, an enum of nothing but address
     * fields, so a `value` sitting next to a `field` is always PII. `field` itself
     * stays readable, which is the part worth debugging with: it tells you which
     * field the customer just edited.
     */
    const hasAddressFieldSibling = entries.some(([key]) => key === 'field')

    return entries.reduce((result, [key, entry]) => {
      const isPii =
        PII_BODY_KEYS.has(key.toLowerCase()) || (hasAddressFieldSibling && key === 'value')
      result[key] = isPii ? REDACTED : redactValue(entry)
      return result
    }, {} as Record<string, unknown>)
  }
  return value
}

/**
 * Anything that isn't JSON can't be redacted field by field - a form-encoded or
 * opaque body is replaced rather than guessed at.
 */
function redactBody(body?: string | null) {
  if (!body) return body
  try {
    return JSON.stringify(redactValue(JSON.parse(body)))
  } catch {
    return REDACTED_OPAQUE_BODY
  }
}

export function filterCapturedNetworkRequest<T extends NetworkRequestLike>(request: T): T | null {
  const url = request.name ?? ''
  const { hostname, pathname } = parseUrl(url)

  if (isDeniedHost(hostname)) return null
  if (DENIED_PATH_PATTERNS.some((pattern) => pattern.test(pathname))) return null

  const withoutPayload = {
    ...request,
    name: stripQueryString(url),
    requestHeaders: undefined,
    responseHeaders: undefined,
    requestBody: REDACTED_BODY,
    responseBody: REDACTED_BODY
  }

  if (NEVER_PAYLOAD_MATCHERS.some((matcher) => matcher.test(pathname))) return withoutPayload

  if (REDACTED_MATCHERS.some((matcher) => matcher.test(pathname))) {
    return {
      ...request,
      name: stripQueryString(url),
      requestHeaders: redactHeaders(request.requestHeaders),
      responseHeaders: redactHeaders(request.responseHeaders),
      requestBody: redactBody(request.requestBody),
      responseBody: redactBody(request.responseBody)
    }
  }

  // Fail closed: an endpoint nobody has reasoned about never carries a payload.
  return withoutPayload
}
